import api from '@/api'
import Azure from '@/lib/Azure'
import ReverMedia from '@/lib/ReverMediaSDK'
import { endOrgSwitchHandoff, startOrgSwitchHandoff } from '@/lib/orgSwitchHandoff'
import { isSafeRedirect } from '@/lib/safeRedirect'
import { removePersistedOrgSession } from '@/store/persistence'
import constants from '@/constants'
import types from './accessTypes.js'

const SET_LANGUAGE = 'setLanguage'
const AZURE_STORAGE_PROVIDER = 'EXTERNAL_AZURE_STORAGE_SERVICE'
const ORG_SWITCH_PLACEBO_DURATION_MS = 3000

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

export default {
  setLogin(context, loginResponse) {
    const { account, accessToken, userId } = loginResponse
    const { site, organization, tags = [] } = account

    context.commit(types.SET_ACCOUNT, { account })
    context.commit(types.SET_TOKEN, { token: accessToken })
    context.commit(types.SET_USER_ID, { userId })
    context.commit(types.SET_ORGANIZATION_ID, {
      organizationId: organization?._id ?? '',
    })
    context.commit(types.SET_SITE_ID, { siteId: site?._id ?? '' })
    context.commit(types.SET_TAGS, { tags: tags.map(tag => tag?._id ?? tag) })
    context.commit(types.SET_ORGANIZATION, { organization })
    context.commit(types.SET_LOGO, organization?.logo ?? null)

    context.dispatch('setOrganizationCurrency', {
      currency: organization?.currency ?? '',
    })
    context.dispatch('fetchOrganizationAISettings')
  },

  async getMe(context) {
    const response = await api.account.getMe()
    const me = response.data
    const { _id, organization, site, reverScore, language, tags = [] } = me

    context.commit(types.SET_ACCOUNT, { account: me })
    context.commit(types.SET_USER_ID, { userId: _id })
    context.commit(types.SET_ORGANIZATION, { organization })
    context.commit(types.SET_ACCOUNT_POINTS, reverScore)
    context.commit(types.SET_LOGO, organization?.logo ?? null)
    context.commit(types.SET_SITE_ID, { siteId: site?._id ?? '' })
    context.commit(types.SET_TAGS, { tags: tags.map(tag => tag?._id ?? tag) })
    context.commit(types.SET_ORGANIZATION_ID, {
      organizationId: organization?._id ?? '',
    })

    context.dispatch('getOrganizationStructure', {
      organizationId: organization._id,
    })
    context.dispatch('fetchOrganizationAISettings')
    context.dispatch(SET_LANGUAGE, { language })
    context.dispatch('setOrganizationCurrency', {
      currency: organization.currency || '',
    })

    return me
  },

  refreshAccountData(context) {
    return api.account
      .getMe()
      .then(response => {
        context.commit(types.SET_ACCOUNT, {
          account: response.data,
        })

        context.commit(types.SET_USER_ID, {
          userId: response.data._id,
        })

        context.commit(types.SET_ORGANIZATION_ID, {
          organizationId: response.data.organization._id.toString(),
        })

        context.commit(types.SET_ORGANIZATION, {
          organization: response.data.organization,
        })

        context.commit(types.SET_ACCOUNT_POINTS, response.data.reverScore)

        return response.data
      })
      .catch(err => {
        throw err
      })
  },

  setToken(context, token) {
    context.commit(types.SET_TOKEN, {
      token: token,
    })
  },

  destroySession(context) {
    context.commit(types.CLEAR_ACCOUNT)
    context.commit(types.CLEAR_USER_ID)
    context.commit(types.CLEAR_TOKEN)
    context.commit(types.CLEAR_ORGANIZATION_ID)
    context.commit(types.CLEAR_ORGANIZATION)
    context.commit(types.CLEAR_LOGO)
    context.commit(types.CLEAR_TAGS)
    context.commit(types.CLEAR_ACCOUNT_ORGANIZATIONS)
    context.commit(types.CLEAR_PENDING_ORG_SWITCH)

    window.localStorage.clear()

    const language =
      navigator.language.substring(0, 2) ||
      navigator.userLanguage.substring(0, 2)

    context.dispatch('setLanguage', {
      language: language,
    })
  },

  async logout(context) {
    try {
      const response = await api.account.logout()
      window.localStorage.clear()
      return response
    } catch (error) {
      return error
    } finally {
      context.dispatch('destroySession')
    }
  },

  updateOwnAccount(context, payload) {
    return api.account
      .update(payload.organizationId, payload.userId, payload.data)
      .then(response => {
        context.dispatch('setLanguage', {
          language: response.data.language,
        })

        context.commit(types.SET_ACCOUNT, {
          account: response.data,
        })

        context.dispatch('refreshAccountData')

        return response
      })
  },

  async redirectToAzureLogin(_context, payload = {}) {
    await Azure.loginRedirect({
      email: payload?.email,
      authorityURL: payload?.authorityURL,
    })
  },

  async loginWithAzureToken(context, payload) {
    const { accessToken, expiresOn } = payload
    const { data: reverSession } = await api.account.loginWithAzure({
      token: accessToken,
    })

    // Organizations might login using Azure, but they could use AWS or any other service as storage provider. In those scenarios we should not check the Azure storage token validity.
    const requiresAzureStorageAuth =
      reverSession?.account?.organization?.mediaProvider ===
      AZURE_STORAGE_PROVIDER

    const azureStorageSession = requiresAzureStorageAuth
      ? await Azure.requestStorageAuth()
      : null

    const azureData = {
      session: { accessToken, expiresOn },
      storage: azureStorageSession,
    }

    context.commit('SET_AZURE', azureData)
    // We need to set the azure object in the localstorage to use it in the refresh token function
    localStorage.setItem('azure', JSON.stringify(azureData))

    return reverSession
  },

  refreshAzureTokens(context, payload) {
    context.commit('SET_AZURE', payload)
    // And also here, when the token changes whe need to set the azure object again in the localstorage to use it in the refresh token function
    localStorage.setItem('azure', JSON.stringify(payload))
    ReverMedia.setAzureStorageToken(payload.storage.accessToken)
  },

  updateLocationAttributes(context, payload) {
    context.commit('UPDATE_ACCOUNT_LOCATION', payload.location)

    return api.account.update(
      payload.organizationId,
      payload.accountId,
      payload.location
    )
  },

  updateAccountNotificationSettings(context, payload) {
    return api.account.update(
      payload.organizationId,
      payload.accountId,
      payload.data
    )
  },

  async acceptTermsOfService(context, payload) {
    await api.account.update(payload.organizationId, payload.accountId, {
      tocsHasAccepted: true,
    })

    context.commit('ACCEPT_TERMS_OF_SERVICE')
  },

  async fetchOrganizationAISettings(context) {
    try {
      const organizationHasAIFeatures = Boolean(
        context.getters.organization?.featuresEnabled__aiFeatures
      )

      // Backend returns a 403 error if the organization does not have AI features enabled. We just early return an empty object to prevent unhandled errors logs.
      if (!organizationHasAIFeatures) {
        context.dispatch('setOrganizationAISettings', {})
        return {}
      }

      const response = await api.ai.getOrganizationAISettings(
        context.getters.organizationId
      )

      context.dispatch('setOrganizationAISettings', response.data ?? {})
      return response.data
    } catch (error) {
      console.error('Error fetching organization AI settings:', error)
      context.dispatch('setOrganizationAISettings', {})
      return {}
    }
  },

  async updateOrganizationAISettings(context, payload) {
    const response = await api.ai.updateOrganizationAISettings(
      payload.organizationId,
      payload.organizationAISettings
    )

    context.dispatch('setOrganizationAISettings', response.data ?? {})

    return response.data
  },

  setOrganizationAISettings(context, aiSettings) {
    context.commit(types.SET_ORGANIZATION_AI_SETTINGS, aiSettings)
  },

  async fetchAccountOrganizations(context) {
    const requestedToken = context.getters.token

    try {
      const response = await api.account.getAccountOrganizations()
      if (context.getters.token !== requestedToken) return []

      const organizations = response.data ?? []
      context.commit(types.SET_ACCOUNT_ORGANIZATIONS, { organizations })
      context.commit(types.SET_HAS_FETCHED_ACCOUNT_ORGANIZATIONS, true)
      return organizations
    } catch (error) {
      if (context.getters.token === requestedToken) {
        context.commit(types.SET_HAS_FETCHED_ACCOUNT_ORGANIZATIONS, true)
      }
      throw error
    }
  },

  async switchOrganization(context, payload = {}) {
    const {
      organizationId,
      shouldJoin = false,
      redirectTo = '/home',
      organizationName = null,
    } = payload

    if (context.getters.isSwitchingOrganizations) return

    const safeRedirectTo = isSafeRedirect(redirectTo) ? redirectTo : '/home'

    const rotateSession = async () => {
      const { data: session } = await api.account.switchOrganization(
        organizationId
      )

      window.localStorage.setItem('token', session.accessToken)

      if (shouldJoin) {
        try {
          await api.account.update(organizationId, session.userId, {
            status: constants.accountStatuses.JOINED,
          })
        } catch (error) {
          console.error('Error joining invited organization:', error)
        }
      }

      removePersistedOrgSession()
    }

    context.commit(types.SET_ORG_SWITCH_SPLASH, { organizationName })
    startOrgSwitchHandoff()

    try {
      await Promise.all([
        delay(ORG_SWITCH_PLACEBO_DURATION_MS),
        rotateSession(),
      ])

      window.location.replace(safeRedirectTo)
    } catch (error) {
      endOrgSwitchHandoff()
      context.commit(types.CLEAR_ORG_SWITCH_SPLASH)
      throw error
    }
  },

  clearPendingOrgSwitch(context) {
    context.commit(types.CLEAR_PENDING_ORG_SWITCH)
  },
}
